import {
  aiCommands,
  defaultAiGroupLabels,
} from "@smartsuite/smartdoc/lib/esm/extensions/ai/constants";
import {
  AiExtensionService,
  AiUsageParams,
} from "@smartsuite/smartdoc/lib/esm/extensions/ai/service";
import { AiCommand, AiCommandGroupLabels } from "@smartsuite/smartdoc/lib/esm/extensions/ai/types";

import { getEnvBaseUrl } from "../../config/env-url-map";

interface AiResponse {
  result?: string;
  error?: string;
}

type SSAiExtensionServiceOptions = {
  env: string;
  accessToken: string;
  accountSlug: string;
  usageParams: AiUsageParams;
};

const markdownGuard = {
  role: "system",
  content: `You execute the user instructions in the text ## or quotes "".
The response/result should always be in basic markdown format without classes and styles. For example:
Prompt: Give me a list of fruits
Response:
# A list of fruits:

- Apple
- Banana
- Orange

Prompt: Give me a table with two columns and two rows
Response:
| Column 1 | Column 2 |
| -------- | -------- |
| Row 1    | Row 1    |
| Row 2    | Row 2    |

remember:
- do not repeat the text from the prompt between ## or quotes "" as part of the response.
- do not put the result in quotes.
- at least one line break is required between paragraphs and titles. The markdown flavor is commonmark.
`,
};

const buildMarkdownPrompt = (prompt: string): string => {
  return `${markdownGuard.content}\n\n${prompt}`;
};

const AI_BASE_PATH = "/api/v1/ai/";
const AI_STREAM_PATH = "/api/v1/ai/doc/";
const HTTP_STATUS_NOT_ALLOWED = 403;
const HTTP_STATUS_UNPROCESSABLE_ENTITY = 422;
const HTTP_INTERNAL_SERVER_ERROR = 500;
const usageLimitText =
  "You have reached your usage limit on AI features. Add your own OpenAI API key to continue using AI features.";
const disabledText = "AI features are disabled. Enable it in the workspace admin settings.";

const getBaseUrl = (env: string, path: string): string => {
  return `${getEnvBaseUrl(env)}${path}`;
};

export class SSAiExtensionService implements AiExtensionService {
  contentMode: "html" | "markdown" = "markdown";

  private readonly env: string;
  private readonly accessToken: string;
  private readonly accountSlug: string;
  private readonly usageParams: AiUsageParams;

  constructor({ env, accessToken, accountSlug, usageParams }: SSAiExtensionServiceOptions) {
    this.env = env;
    this.accessToken = accessToken;
    this.accountSlug = accountSlug;
    this.usageParams = usageParams;
    this.prompt = this.prompt.bind(this);
    this.stream = this.stream.bind(this);
    this.getCommands = this.getCommands.bind(this);
    this.getGroupLabels = this.getGroupLabels.bind(this);
    this.getUsageParams = this.getUsageParams.bind(this);
  }

  async prompt(prompt: string): Promise<string> {
    const response = await fetch(getBaseUrl(this.env, AI_BASE_PATH), {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({
        prompt: buildMarkdownPrompt(prompt),
      }),
    });

    await this.assertResponse(response);

    const payload = (await response.json()) as AiResponse;
    return payload.result ?? "";
  }

  async stream(prompt: string, callback: (result: string) => void): Promise<void> {
    const response = await fetch(getBaseUrl(this.env, AI_STREAM_PATH), {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({
        messages: [
          markdownGuard,
          {
            role: "user",
            content: prompt,
          },
        ],
      }),
    });

    await this.assertResponse(response);

    if (!response.body) {
      throw new Error("Empty response body");
    }

    const reader = response.body.getReader();
    const textDecoder = new TextDecoder("utf-8");

    while (true) {
      const { value, done } = await reader.read();

      if (done) {
        return;
      }

      if (value) {
        callback(textDecoder.decode(value));
      }
    }
  }

  getCommands(): AiCommand[] {
    return [...aiCommands];
  }

  getGroupLabels(): AiCommandGroupLabels {
    return defaultAiGroupLabels;
  }

  getUsageParams(): AiUsageParams {
    return this.usageParams;
  }

  private getHeaders(): HeadersInit {
    return {
      "Content-Type": "application/json",
      "ACCOUNT-ID": this.accountSlug,
      Authorization: `Bearer ${this.accessToken}`,
    };
  }

  private async assertResponse(response: Response): Promise<void> {
    if (response.status === HTTP_STATUS_NOT_ALLOWED) {
      throw new Error(usageLimitText);
    }

    if (response.status === HTTP_STATUS_UNPROCESSABLE_ENTITY) {
      throw new Error(disabledText);
    }

    if (response.status === HTTP_INTERNAL_SERVER_ERROR) {
      throw new Error((await this.getErrorMessage(response)) ?? "Internal Server Error");
    }

    if (!response.ok) {
      throw new Error(
        (await this.getErrorMessage(response)) ?? `AI request failed with status ${response.status}`
      );
    }
  }

  private async getErrorMessage(response: Response): Promise<string | null> {
    try {
      const payload = (await response.clone().json()) as AiResponse;

      if (typeof payload.error === "string" && payload.error) {
        return payload.error;
      }
    } catch {}

    try {
      const payload = await response.clone().text();

      return payload || null;
    } catch {
      return null;
    }
  }
}
